import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  RefreshCw,
  Search,
  Download,
  Check,
  Trash2,
  Mail,
  User,
  Phone,
  ChevronRight,
  ShieldCheck,
  Link as LinkIcon,
  Copy,
  Eye,
  MessageCircle,
  Send,
  X,
  Plus,
  StickyNote,
  Users,
  Inbox,
  Image as ImageIcon,
  ArrowUp,
  ArrowDown,
  UploadCloud,
  FileText,
  Loader2,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "woodies_admin_token";

const getToken = () => localStorage.getItem(TOKEN_KEY);
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

/* ============== LOGIN ============== */
export const AdminLogin = () => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (getToken()) navigate("/admin");
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Welcome back, admin.");
      navigate("/admin");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grain flex items-center justify-center p-6">
      <Toaster theme="dark" position="top-center" />
      <div className="w-full max-w-md">
        <div className="font-display mb-2 inline-flex items-center gap-3">
          <img src="/woodies-logo.svg" alt="Woodies" className="logo-on-dark h-16 w-16" />
          <span className="text-3xl">WOODIES<span className="text-[#FF4500]">.</span></span>
        </div>
        <div className="label-mono mb-10">/ ADMIN CONTROL ROOM</div>

        <h1 className="font-display-tight text-5xl md:text-6xl uppercase leading-[0.9]">
          Sign in.
        </h1>
        <p className="text-white/60 mt-3">
          Enter the admin password to review submissions and notifications.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="admin-login-form">
          <div>
            <div className="label-mono mb-2">Admin password</div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              data-testid="admin-password-input"
              className="w-full bg-black/40 border border-white/20 px-4 h-12 text-white outline-none focus:border-[#FF4500] focus:ring-2 focus:ring-[#FF4500]"
            />
          </div>
          {err && (
            <div className="text-[#FF4500] label-mono" data-testid="admin-login-error">
              {err}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-brutal bg-[#FF4500] text-black font-display text-lg w-full h-14 inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading ? "AUTHENTICATING..." : "ENTER"} <ChevronRight size={22} strokeWidth={3} />
          </button>
        </form>

        <a href="/" className="label-mono mt-10 inline-block hover:text-[#FF4500]">
          ← Back to site
        </a>
      </div>
    </div>
  );
};

/* ============== SHARE LINK + RECENT VISITS ============== */
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

const ShareLinkGenerator = () => {
  const [name, setName] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const slug = slugify(name);
  const base = (typeof window !== "undefined" && window.location.origin) || "";
  const url = slug ? `${base}/deck?src=${slug}` : `${base}/deck`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — drop it in your investor email.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy. Select & copy manually.");
    }
  };

  return (
    <div className="border border-white/10 bg-[#0c0c0d] p-5 md:p-6" data-testid="share-link-generator">
      <div className="flex items-center gap-2 mb-3">
        <LinkIcon size={16} className="text-[#FFC107]" />
        <span className="label-mono text-[#FFC107]">— TRACKED DECK LINK</span>
      </div>
      <div className="font-display text-2xl mb-3">Build an attribution link.</div>
      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-stretch">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Investor / source name (e.g. Ravi Sequoia)"
          data-testid="share-link-input"
          className="bg-black/40 border border-white/20 px-4 h-12 text-white outline-none focus:border-[#FF4500] focus:ring-2 focus:ring-[#FF4500]"
        />
        <button
          onClick={copy}
          data-testid="share-link-copy"
          className="btn-brutal bg-[#FF4500] text-black font-display px-5 inline-flex items-center justify-center gap-2 h-12"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "COPIED" : "COPY LINK"}
        </button>
      </div>
      <div className="mt-3 font-mono text-sm break-all text-white/80 bg-black/40 border border-white/10 px-3 py-2" data-testid="share-link-preview">
        {url}
      </div>
      <div className="text-xs text-white/40 mt-2">
        Each unique <code className="font-mono text-[#FFC107]">?src=</code> tag is logged below when the investor opens the deck.
      </div>
    </div>
  );
};

const RecentDeckVisits = () => {
  const [visits, setVisits] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const fetchVisits = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/deck-visits`, authHeader());
      setVisits(data.items || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchVisits();
  }, []);

  return (
    <div className="border border-white/10 bg-[#0c0c0d]" data-testid="recent-deck-visits">
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
        <div className="flex items-center gap-2">
          <Eye size={16} className="text-[#FF4500]" />
          <span className="label-mono text-[#FF4500]">— RECENT DECK VIEWS</span>
        </div>
        <button onClick={fetchVisits} className="text-white/40 hover:text-white" aria-label="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>
      <div className="max-h-[360px] overflow-y-auto">
        {loading ? (
          <div className="px-5 py-8 text-white/40 label-mono text-center">LOADING...</div>
        ) : visits.length === 0 ? (
          <div className="px-5 py-8 text-white/40 label-mono text-center">NO VIEWS YET. SHARE A LINK ABOVE.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/50 text-white/50">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs">WHEN</th>
                <th className="text-left px-4 py-2 font-mono text-xs">SOURCE</th>
                <th className="text-left px-4 py-2 font-mono text-xs hidden md:table-cell">REFERRER</th>
                <th className="text-left px-4 py-2 font-mono text-xs hidden lg:table-cell">IP</th>
              </tr>
            </thead>
            <tbody>
              {visits.slice(0, 100).map((v) => (
                <tr key={v.id} className="border-t border-white/5">
                  <td className="px-4 py-2 text-white/80 whitespace-nowrap">
                    {new Date(v.viewed_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">
                    {v.source && v.source !== "direct" ? (
                      <span className="bg-[#FF4500] text-black label-mono px-2 py-0.5">{v.source}</span>
                    ) : (
                      <span className="text-white/30 label-mono">direct</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-white/50 truncate max-w-[260px] hidden md:table-cell">
                    {v.referrer || "—"}
                  </td>
                  <td className="px-4 py-2 text-white/40 font-mono text-xs hidden lg:table-cell">
                    {v.ip || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ============== NEWSLETTER TAB ============== */
const NewsletterTab = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/newsletter`, authHeader());
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const filtered = useMemo(
    () =>
      items.filter((i) => {
        if (!search) return true;
        return (i.email || "").toLowerCase().includes(search.toLowerCase());
      }),
    [items, search]
  );

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from the list?`)) return;
    try {
      await axios.delete(`${API}/admin/newsletter`, { ...authHeader(), data: { email } });
      setItems((prev) => prev.filter((x) => x.email !== email));
      toast.success("Removed");
      if (onChange) onChange();
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    const headers = ["email", "subscribed_at", "source"];
    const csv = [
      headers.join(","),
      ...filtered.map((i) => headers.map((h) => `"${String(i[h] ?? "").replace(/"/g, '""')}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `woodies-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-6" data-testid="newsletter-tab">
      <div className="border border-white/10 bg-[#0c0c0d]">
        <div className="border-b border-white/10 px-5 py-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-[#FFC107]" />
            <span className="label-mono text-[#FFC107]">— SUBSCRIBERS · {items.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center border border-white/20 px-3 h-9">
              <Search size={12} className="text-white/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email"
                data-testid="newsletter-search"
                className="bg-transparent outline-none text-sm flex-1 ml-2 placeholder:text-white/30 w-48"
              />
            </div>
            <button onClick={fetch} className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] p-2" aria-label="Refresh" data-testid="newsletter-refresh">
              <RefreshCw size={14} />
            </button>
            <button onClick={exportCsv} data-testid="newsletter-export" className="bg-[#FFC107] text-black font-display px-3 h-9 inline-flex items-center gap-2 hover:bg-[#FF4500]">
              <Download size={14} /> CSV
            </button>
          </div>
        </div>
        {loading ? (
          <div className="px-5 py-12 text-white/40 label-mono text-center">LOADING...</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-12 text-white/40 label-mono text-center">NO SUBSCRIBERS YET.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/50 text-white/50">
              <tr>
                <th className="text-left px-4 py-2 font-mono text-xs">EMAIL</th>
                <th className="text-left px-4 py-2 font-mono text-xs hidden sm:table-cell">JOINED</th>
                <th className="text-left px-4 py-2 font-mono text-xs hidden lg:table-cell">SOURCE</th>
                <th className="text-right px-4 py-2 font-mono text-xs">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => (
                <tr key={s.email} className="border-t border-white/5" data-testid={`newsletter-row-${s.email}`}>
                  <td className="px-4 py-3 text-white/90">
                    <a href={`mailto:${s.email}`} className="hover:text-[#FF4500]">{s.email}</a>
                  </td>
                  <td className="px-4 py-3 text-white/60 hidden sm:table-cell">
                    {s.subscribed_at ? new Date(s.subscribed_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-white/40 font-mono text-xs hidden lg:table-cell">
                    {s.source || "footer"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => remove(s.email)}
                      data-testid={`newsletter-delete-${s.email}`}
                      className="border border-white/15 hover:border-[#FF4500] hover:text-[#FF4500] p-2"
                      aria-label="Remove"
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};


/* ============== REGENERATE DECK PDF BUTTON ============== */
const RegenerateDeckPdfButton = () => {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/deck/regenerate-pdf`, {}, authHeader());
      const kb = Math.round((data.size_bytes || 0) / 1024);
      toast.success(`Deck PDF refreshed (${kb} KB). Click "Save to Github" to ship it.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF regenerate failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={click}
      disabled={busy}
      data-testid="regenerate-deck-pdf"
      title="Re-render the pitch deck PDF from the live site (commit via 'Save to Github')"
      className={`ml-auto self-center px-4 py-2 label-mono inline-flex items-center gap-2 border ${
        busy ? "border-white/15 text-white/40" : "border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107] hover:text-black"
      }`}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
      {busy ? "RENDERING…" : "REGENERATE DECK PDF"}
    </button>
  );
};


/* ============== MENU TAB (CMS) ============== */
const MenuTab = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCat, setUploadCat] = useState("chicken");
  const [dirty, setDirty] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/menu`, authHeader());
      setItems(data.items || []);
      setCategories(data.categories || []);
      setDirty(false);
    } catch (e) {
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const move = (idx, delta) => {
    const next = [...items];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    setDirty(true);
  };

  const changeCat = (idx, cat) => {
    const next = [...items];
    next[idx] = { ...next[idx], cat };
    setItems(next);
    setDirty(true);
  };

  const remove = async (hash) => {
    if (!window.confirm("Remove this item from the menu? (file kept on disk)")) return;
    try {
      await axios.delete(`${API}/admin/menu/${hash}`, authHeader());
      toast.success("Item removed");
      fetchAll();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const saveOrder = async () => {
    try {
      await axios.put(`${API}/admin/menu`, { items: items.map(({ hash, cat }) => ({ hash, cat })) }, authHeader());
      toast.success("Menu saved");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("cat", uploadCat);
      // Don't set Content-Type explicitly — let axios+browser set multipart boundary.
      await axios.post(`${API}/admin/menu/upload`, fd, authHeader());
      toast.success("Item uploaded");
      e.target.value = "";
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-6" data-testid="menu-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="label-mono text-[#FF4500]">/ MENU CMS</div>
          <h3 className="font-display text-2xl mt-1">{items.length} items · {categories.length} categories</h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={uploadCat}
            onChange={(e) => setUploadCat(e.target.value)}
            data-testid="menu-upload-cat"
            className="bg-[#0c0c0d] border border-white/15 px-3 py-2 label-mono text-white"
          >
            {categories.filter((c) => c.id !== "all").map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
          <label
            data-testid="menu-upload-btn"
            className={`px-4 py-2 label-mono border inline-flex items-center gap-2 cursor-pointer ${
              uploading ? "border-white/15 text-white/40" : "border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black"
            }`}
          >
            <UploadCloud size={14} /> {uploading ? "UPLOADING…" : "UPLOAD"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              disabled={uploading}
              onChange={onFile}
            />
          </label>
          <button
            onClick={fetchAll}
            data-testid="menu-refresh"
            className="px-4 py-2 label-mono border border-white/15 hover:border-white inline-flex items-center gap-2"
          >
            <RefreshCw size={14} /> REFRESH
          </button>
          <button
            onClick={saveOrder}
            disabled={!dirty}
            data-testid="menu-save"
            className={`px-4 py-2 label-mono inline-flex items-center gap-2 ${
              dirty ? "bg-[#FF4500] text-black" : "bg-white/5 text-white/30 cursor-not-allowed"
            }`}
          >
            <Check size={14} /> SAVE ORDER {dirty && "·"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 py-10 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-white/50 py-10 text-center border border-dashed border-white/15">
          No items. Upload your first one.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m, idx) => (
            <div
              key={m.hash}
              className="border border-white/10 bg-[#0c0c0d] hover:border-[#FF4500]/50 transition-colors"
              data-testid={`menu-row-${m.hash}`}
            >
              <div className="aspect-square bg-black overflow-hidden">
                <img
                  src={m.image_url ? `${BACKEND_URL}${m.image_url}` : `/menu/${m.hash}.jpg`}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-3 space-y-2">
                <div className="font-mono text-[10px] text-white/40 truncate" title={m.hash}>{m.hash}</div>
                <select
                  value={m.cat}
                  onChange={(e) => changeCat(idx, e.target.value)}
                  data-testid={`menu-row-cat-${m.hash}`}
                  className="w-full bg-black border border-white/15 px-2 py-1 label-mono text-xs"
                >
                  {categories.filter((c) => c.id !== "all").map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                <div className="flex items-center justify-between gap-1">
                  <div className="flex gap-1">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      data-testid={`menu-row-up-${m.hash}`}
                      className="p-1.5 border border-white/15 hover:border-white disabled:opacity-30"
                      aria-label="Move up"
                    ><ArrowUp size={14} /></button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === items.length - 1}
                      data-testid={`menu-row-down-${m.hash}`}
                      className="p-1.5 border border-white/15 hover:border-white disabled:opacity-30"
                      aria-label="Move down"
                    ><ArrowDown size={14} /></button>
                  </div>
                  <span className="font-mono text-[10px] text-white/30">#{idx + 1}</span>
                  <button
                    onClick={() => remove(m.hash)}
                    data-testid={`menu-row-delete-${m.hash}`}
                    className="p-1.5 border border-white/15 hover:border-red-500 hover:text-red-400"
                    aria-label="Delete"
                  ><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ============== BLOG TAB (CMS) ============== */
const BlogTab = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | post object

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`, authHeader());
      setPosts(data.items || []);
    } catch (e) {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${id}`, authHeader());
      toast.success("Post deleted");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (editing) {
    return (
      <BlogEditor
        existing={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchAll(); }}
      />
    );
  }

  return (
    <div className="mt-6" data-testid="blog-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="label-mono text-[#FF4500]">/ BLOG CMS</div>
          <h3 className="font-display text-2xl mt-1">{posts.length} post{posts.length === 1 ? "" : "s"}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            data-testid="blog-refresh"
            className="px-4 py-2 label-mono border border-white/15 hover:border-white inline-flex items-center gap-2"
          >
            <RefreshCw size={14} /> REFRESH
          </button>
          <button
            onClick={() => setEditing("new")}
            data-testid="blog-new-btn"
            className="px-4 py-2 label-mono bg-[#FF4500] text-black hover:bg-[#FFC107] inline-flex items-center gap-2"
          >
            <FileText size={14} /> NEW POST
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/50 py-10 text-center">Loading…</div>
      ) : posts.length === 0 ? (
        <div className="text-white/50 py-12 text-center border border-dashed border-white/15">
          <p>No posts yet.</p>
          <button
            onClick={() => setEditing("new")}
            className="mt-4 px-4 py-2 label-mono bg-[#FF4500] text-black"
          >+ WRITE YOUR FIRST POST</button>
        </div>
      ) : (
        <div className="border border-white/10">
          {posts.map((p) => (
            <div
              key={p.id}
              className="flex flex-wrap items-center gap-4 p-4 border-b border-white/5 hover:bg-white/5"
              data-testid={`blog-row-${p.slug}`}
            >
              <div className="w-16 h-16 bg-[#0c0c0d] border border-white/10 flex-shrink-0 overflow-hidden">
                {p.cover_image ? <img src={p.cover_image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">—</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`label-mono text-[10px] px-2 py-0.5 ${p.status === "published" ? "bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30" : "bg-white/5 text-white/40 border border-white/15"}`}>
                    {p.status?.toUpperCase()}
                  </span>
                  <span className="label-mono text-white/40 text-[10px]">{p.published_at ? new Date(p.published_at).toLocaleDateString() : "—"}</span>
                  {p.tags?.length > 0 && <span className="label-mono text-white/40 text-[10px]">·{p.tags.slice(0, 3).map((t) => `#${t}`).join(" ")}</span>}
                </div>
                <div className="font-display uppercase text-lg leading-tight mt-1 truncate">{p.title}</div>
                <div className="text-white/40 text-xs truncate">/blog/{p.slug}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={`/blog/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 border border-white/15 hover:border-white"
                  title="Open public link"
                  data-testid={`blog-row-view-${p.slug}`}
                ><ArrowUp size={14} className="rotate-45" /></a>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await axios.get(`${API}/admin/blog/posts/${p.id}`, authHeader());
                      setEditing(data);
                    } catch { toast.error("Could not open post"); }
                  }}
                  className="px-3 py-2 label-mono border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black"
                  data-testid={`blog-row-edit-${p.slug}`}
                >EDIT</button>
                <button
                  onClick={() => remove(p.id)}
                  className="p-2 border border-white/15 hover:border-red-500 hover:text-red-400"
                  data-testid={`blog-row-delete-${p.slug}`}
                  aria-label="Delete"
                ><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ============== BLOG EDITOR ============== */
const BlogEditor = ({ existing, onCancel, onSaved }) => {
  const [title, setTitle] = useState(existing?.title || "");
  const [slug, setSlug] = useState(existing?.slug || "");
  const [excerpt, setExcerpt] = useState(existing?.excerpt || "");
  const [contentHtml, setContentHtml] = useState(existing?.content_html || "");
  const [coverImage, setCoverImage] = useState(existing?.cover_image || "");
  const [tags, setTags] = useState((existing?.tags || []).join(", "));
  const [status, setStatus] = useState(existing?.status || "draft");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const wordCount = useMemo(() => {
    return (contentHtml.replace(/<[^>]+>/g, " ").trim().split(/\s+/).filter(Boolean).length);
  }, [contentHtml]);

  const onCoverSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/blog/upload`, fd, authHeader());
      setCoverImage(data.path);
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!title.trim() || contentHtml.trim().length < 20) {
      toast.error("Title + at least a couple of sentences required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        slug: slug.trim(),
        excerpt: excerpt.trim(),
        content_html: contentHtml,
        cover_image: coverImage,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        status,
      };
      if (existing?.id) {
        await axios.put(`${API}/admin/blog/posts/${existing.id}`, payload, authHeader());
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, authHeader());
        toast.success(`Post created (${status})`);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6" data-testid="blog-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="label-mono text-[#FF4500]">/ {existing ? "EDIT POST" : "NEW POST"}</div>
          <h3 className="font-display text-2xl mt-1">{existing ? existing.title : "Write something good."}</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-4 py-2 label-mono border border-white/15 hover:border-white" data-testid="blog-editor-cancel">CANCEL</button>
          <button
            onClick={() => setShowPreview((v) => !v)}
            className="px-4 py-2 label-mono border border-white/15 hover:border-white"
            data-testid="blog-editor-preview-toggle"
          >{showPreview ? "EDIT" : "PREVIEW"}</button>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="blog-editor-status"
            className="bg-[#0c0c0d] border border-white/15 px-3 py-2 label-mono text-white"
          >
            <option value="draft">DRAFT</option>
            <option value="published">PUBLISHED</option>
          </select>
          <button
            onClick={save}
            disabled={saving}
            data-testid="blog-editor-save"
            className={`px-5 py-2 label-mono inline-flex items-center gap-2 ${saving ? "bg-white/10 text-white/40" : "bg-[#FF4500] text-black hover:bg-[#FFC107]"}`}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>

      {showPreview ? (
        <div className="border border-white/10 p-6 bg-[#0c0c0d]">
          {coverImage && <img src={coverImage} alt="" className="w-full max-h-[300px] object-cover mb-6" />}
          <h1 className="font-display-tight text-3xl uppercase">{title || "Untitled"}</h1>
          {excerpt && <p className="mt-3 text-white/65 border-l-2 border-[#FF4500] pl-3">{excerpt}</p>}
          <div className="mt-6 blog-content prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: contentHtml || "<em>(empty)</em>" }} />
          <style>{`
            .blog-content h1, .blog-content h2, .blog-content h3 { font-family: 'Bebas Neue', system-ui; color: #fff; margin: 1.5rem 0 0.5rem; }
            .blog-content h2 { font-size: 1.5rem; border-left: 3px solid #FF4500; padding-left: 0.6rem; }
            .blog-content p { margin: 0.75rem 0; line-height: 1.7; color: rgba(255,255,255,0.82); }
            .blog-content a { color: #FF4500; }
            .blog-content blockquote { border-left: 3px solid #FF4500; padding-left: 1rem; color: rgba(255,255,255,0.65); font-style: italic; }
            .blog-content img { max-width: 100%; height: auto; }
          `}</style>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main editor column */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="label-mono text-white/50 text-xs">TITLE *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="blog-editor-title"
                placeholder="Crispy Chicken: Why we never freeze it"
                className="w-full mt-1 bg-black border border-white/20 focus:border-[#FF4500] outline-none px-4 py-3 text-white text-xl font-display uppercase"
              />
            </div>
            <div>
              <label className="label-mono text-white/50 text-xs">SLUG (URL)</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                data-testid="blog-editor-slug"
                placeholder="auto-generated from title if empty"
                className="w-full mt-1 bg-black border border-white/20 focus:border-[#FF4500] outline-none px-4 py-2 text-white font-mono text-sm"
              />
            </div>
            <div>
              <label className="label-mono text-white/50 text-xs">EXCERPT (≤300 chars)</label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value.slice(0, 300))}
                data-testid="blog-editor-excerpt"
                placeholder="A 1–2 sentence teaser shown on the blog list and as the social preview description."
                rows={3}
                className="w-full mt-1 bg-black border border-white/20 focus:border-[#FF4500] outline-none px-4 py-3 text-white"
              />
              <div className="text-xs text-white/30 mt-1">{excerpt.length}/300</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="label-mono text-white/50 text-xs">CONTENT (HTML — paste from Marblism) *</label>
                <span className="label-mono text-white/30 text-[10px]">{wordCount} words · ~{Math.max(1, Math.round(wordCount / 220))} min read</span>
              </div>
              <textarea
                value={contentHtml}
                onChange={(e) => setContentHtml(e.target.value)}
                data-testid="blog-editor-content"
                placeholder="<p>Paste your post HTML from Marblism here…</p>"
                rows={20}
                className="w-full mt-1 bg-black border border-white/20 focus:border-[#FF4500] outline-none px-4 py-3 text-white font-mono text-sm leading-relaxed"
              />
              <div className="text-xs text-white/40 mt-1">
                Tip: in Marblism, use "Copy as HTML" or "Export → HTML" and paste here. Then click PREVIEW to see how it'll render.
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div>
              <label className="label-mono text-white/50 text-xs">COVER IMAGE</label>
              {coverImage && (
                <div className="mt-2 relative">
                  <img src={coverImage} alt="" className="w-full aspect-[4/3] object-cover border border-white/10" />
                  <button
                    onClick={() => setCoverImage("")}
                    className="absolute top-2 right-2 bg-black/80 text-white/80 hover:text-red-400 p-1 border border-white/20"
                    title="Remove cover"
                  ><Trash2 size={12} /></button>
                </div>
              )}
              <label
                data-testid="blog-editor-cover-upload"
                className={`mt-2 px-4 py-2 label-mono border inline-flex items-center gap-2 cursor-pointer ${
                  uploading ? "border-white/15 text-white/40" : "border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black"
                }`}
              >
                <UploadCloud size={14} /> {uploading ? "UPLOADING…" : (coverImage ? "REPLACE" : "UPLOAD COVER")}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={onCoverSelect} />
              </label>
              <input
                type="text"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="…or paste an image URL"
                className="w-full mt-2 bg-black border border-white/15 focus:border-[#FF4500] outline-none px-3 py-2 text-white text-xs font-mono"
              />
            </div>
            <div>
              <label className="label-mono text-white/50 text-xs">TAGS (comma-separated)</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                data-testid="blog-editor-tags"
                placeholder="recipes, behind-the-kitchen, guwahati"
                className="w-full mt-1 bg-black border border-white/20 focus:border-[#FF4500] outline-none px-4 py-2 text-white text-sm"
              />
            </div>
            <div className="border border-white/10 p-4 text-xs text-white/50 leading-relaxed">
              <div className="label-mono text-white/40 mb-2">/ PUBLISHING TIPS</div>
              <ul className="space-y-1.5 list-disc list-inside marker:text-[#FF4500]">
                <li>Set status to <b className="text-white">PUBLISHED</b> to make the post live.</li>
                <li>Posts auto-appear in the sitemap & on /blog within seconds.</li>
                <li>Cover image becomes the social-share preview (WhatsApp / LinkedIn).</li>
                <li>Tags become clickable filters at /blog?tag=…</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/* ============== DASHBOARD ============== */
const TYPE_META = {
  investor: { label: "INVESTOR", color: "#FF4500" },
  homechef: { label: "HOMECHEF", color: "#FFC107" },
  franchisee: { label: "FRANCHISEE", color: "#ffffff" },
};

const StatCard = ({ label, value, sublabel, accent = "#FF4500" }) => (
  <div className="border border-white/10 bg-[#0c0c0d] p-5 md:p-7">
    <div className="label-mono">{label}</div>
    <div className="font-display text-5xl md:text-6xl mt-2" style={{ color: accent }}>
      {value}
    </div>
    {sublabel && <div className="text-white/50 text-sm mt-1">{sublabel}</div>}
  </div>
);

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [reviewedFilter, setReviewedFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState("submissions"); // submissions | newsletter
  const [picked, setPicked] = useState(new Set());
  const [emailModal, setEmailModal] = useState(null);
  const [noteText, setNoteText] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        axios.get(`${API}/admin/stats`, authHeader()),
        axios.get(`${API}/admin/submissions`, authHeader()),
      ]);
      setStats(s.data);
      setItems(list.data.items);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
      } else {
        toast.error("Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    fetchAll();
  }, [navigate]);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (filter !== "all" && i.type !== filter) return false;
      if (reviewedFilter === "pending" && i.reviewed) return false;
      if (reviewedFilter === "reviewed" && !i.reviewed) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${i.name} ${i.email} ${i.phone} ${i.message || ""} ${i.organization || ""} ${i.location || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filter, reviewedFilter, search]);

  const toggleReview = async (id, current) => {
    try {
      await axios.patch(`${API}/admin/submissions/${id}`, { reviewed: !current }, authHeader());
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, reviewed: !current } : x)));
      if (selected?.id === id) setSelected({ ...selected, reviewed: !current });
      toast.success(!current ? "Marked reviewed" : "Marked pending");
      fetchAll();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, authHeader());
      setItems((prev) => prev.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = () => {
    const headers = [
      "type", "name", "email", "phone", "city", "organization",
      "linkedin_url", "investment_range", "experience", "specialty", "location", "capital",
      "message", "attribution", "reviewed", "submitted_at",
    ];
    const csv = [
      headers.join(","),
      ...filtered.map((i) =>
        headers
          .map((h) => `"${String(i[h] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `woodies-submissions-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== Bulk actions =====
  const togglePick = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const pickAllVisible = () => setPicked(new Set(filtered.map((i) => i.id)));
  const clearPicks = () => setPicked(new Set());

  const bulkAction = async (action) => {
    if (picked.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${picked.size} submission(s)?`)) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/submissions/bulk`,
        { ids: Array.from(picked), action },
        authHeader()
      );
      toast.success(`${data.affected} updated`);
      clearPicks();
      fetchAll();
    } catch {
      toast.error("Bulk action failed");
    }
  };

  // ===== Notes =====
  const addNote = async () => {
    if (!noteText.trim() || !selected) return;
    try {
      const { data } = await axios.post(
        `${API}/admin/submissions/${selected.id}/notes`,
        { text: noteText.trim() },
        authHeader()
      );
      const nextNotes = [...(selected.notes || []), data.note];
      setSelected({ ...selected, notes: nextNotes });
      setItems((prev) => prev.map((x) => (x.id === selected.id ? { ...x, notes: nextNotes } : x)));
      setNoteText("");
      toast.success("Note added");
    } catch {
      toast.error("Could not add note");
    }
  };

  const removeNote = async (noteId) => {
    if (!selected) return;
    try {
      await axios.delete(
        `${API}/admin/submissions/${selected.id}/notes/${noteId}`,
        authHeader()
      );
      const nextNotes = (selected.notes || []).filter((n) => n.id !== noteId);
      setSelected({ ...selected, notes: nextNotes });
      setItems((prev) => prev.map((x) => (x.id === selected.id ? { ...x, notes: nextNotes } : x)));
    } catch {
      toast.error("Could not delete note");
    }
  };

  // ===== Email =====
  const openEmail = async () => {
    if (!selected) return;
    const firstName = (selected.first_name || (selected.name || "").split(" ")[0] || "there").trim() || "there";
    let templates = null;
    try {
      const { data } = await axios.get(`${API}/content`);
      templates = data?.email_templates || null;
    } catch { /* fall back */ }
    const fallback = {
      investor_subject: "Re: Your Woodies inquiry",
      investor_body: `Hi ${firstName},\n\nThanks for your interest in Woodies.`,
      homechef_subject: "Re: Your Woodies HomeChef interest",
      homechef_body: `Hi ${firstName},\n\nThanks for raising your hand to cook with Woodies.`,
      franchisee_subject: "Re: Your Woodies franchise inquiry",
      franchisee_body: `Hi ${firstName},\n\nExcited about your interest in bringing Woodies to a new city.`,
    };
    const t = templates || fallback;
    const type = selected.type || "investor";
    const subjKey = `${type}_subject`;
    const bodyKey = `${type}_body`;
    const subject = (t[subjKey] || fallback[subjKey] || "Following up").replace(/{first_name}/g, firstName);
    const body = (t[bodyKey] || fallback[bodyKey] || "").replace(/{first_name}/g, firstName);
    setEmailModal({ subject, body });
  };

  const sendEmail = async () => {
    if (!emailModal || !selected) return;
    try {
      await axios.post(
        `${API}/admin/submissions/${selected.id}/email`,
        { subject: emailModal.subject, body: emailModal.body },
        authHeader()
      );
      toast.success(`Sent to ${selected.email}`);
      setEmailModal(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
  };

  const buildWhatsAppLink = (sub) => {
    const num = (sub.phone || "").replace(/[^0-9]/g, "");
    const firstName = (sub.name || "").split(" ")[0] || "there";
    const msg = encodeURIComponent(`Hi ${firstName}, this is Team Woodies following up on your inquiry. When's a good time to chat?`);
    return num ? `https://wa.me/${num}?text=${msg}` : null;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white grain">
      <Toaster theme="dark" position="top-center" />

      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/80 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <a href="/" className="font-display text-2xl inline-flex items-center gap-2.5" data-testid="admin-brand-logo">
            <img src="/woodies-logo.svg" alt="Woodies" className="logo-on-dark h-10 w-10" />
            <span>WOODIES<span className="text-[#FF4500]">.</span></span>
            <span className="label-mono ml-3">/ ADMIN</span>
          </a>
          <div className="flex items-center gap-3">
            <a
              href="/admin/content"
              data-testid="nav-edit-content"
              className="border border-[#FFC107]/60 text-[#FFC107] hover:bg-[#FFC107] hover:text-black px-3 py-2 inline-flex items-center gap-2 label-mono"
            >
              EDIT CONTENT
            </a>
            <button
              onClick={fetchAll}
              data-testid="admin-refresh"
              className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] p-2.5"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] px-3 py-2 inline-flex items-center gap-2 label-mono"
            >
              <LogOut size={14} /> LOGOUT
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck size={18} className="text-[#FF4500]" />
          <span className="label-mono text-[#FF4500]">/ DASHBOARD</span>
        </div>
        <h1 className="font-display-tight text-5xl md:text-7xl uppercase leading-[0.9]">
          Submissions
          <span className="text-[#FFC107]">.</span>
        </h1>
        <p className="text-white/60 mt-3 max-w-xl">
          Review every investor, homechef and franchisee inquiry. Auto-emailed
          to {process.env.REACT_APP_NOTIFY_EMAIL || "business@woodiesfan.com"} the moment they hit submit.
        </p>

        {/* Stats */}
        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10" data-testid="admin-stats">
              <StatCard label="TOTAL SUBMISSIONS" value={stats.total_submissions} sublabel={`${stats.pending_total} pending`} accent="#FF4500" />
              <StatCard label="INVESTORS" value={stats.by_type?.investor?.count || 0} sublabel={`${stats.by_type?.investor?.pending || 0} pending`} accent="#ffffff" />
              <StatCard label="HOMECHEFS" value={stats.by_type?.homechef?.count || 0} sublabel={`${stats.by_type?.homechef?.pending || 0} pending`} accent="#FFC107" />
              <StatCard label="FRANCHISEES" value={stats.by_type?.franchisee?.count || 0} sublabel={`${stats.by_type?.franchisee?.pending || 0} pending`} accent="#ffffff" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4" data-testid="admin-deck-stats">
              <StatCard label="DECK VIEWS · TOTAL" value={stats.deck_views || 0} sublabel="all-time" accent="#FF4500" />
              <StatCard label="DECK VIEWS · 7D" value={stats.deck_views_7d || 0} sublabel="last 7 days" accent="#FFC107" />
              <StatCard label="UNIQUE VISITORS" value={stats.deck_unique_visitors || 0} sublabel="distinct IPs" accent="#22c55e" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4 mt-6">
              <ShareLinkGenerator />
              <RecentDeckVisits />
            </div>
          </>
        )}

        {/* Tabs */}
        <div className="mt-10 flex gap-1" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab("submissions")}
            data-testid="tab-submissions"
            className={`px-5 py-3 label-mono inline-flex items-center gap-2 border-b-2 ${
              activeTab === "submissions"
                ? "border-[#FF4500] text-white"
                : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Inbox size={14} /> SUBMISSIONS
            <span className="text-[#FFC107]">·{items.length}</span>
          </button>
          <button
            onClick={() => setActiveTab("newsletter")}
            data-testid="tab-newsletter"
            className={`px-5 py-3 label-mono inline-flex items-center gap-2 border-b-2 ${
              activeTab === "newsletter"
                ? "border-[#FF4500] text-white"
                : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <Users size={14} /> NEWSLETTER
            <span className="text-[#FFC107]">·{stats?.newsletter_count || 0}</span>
          </button>
          <button
            onClick={() => setActiveTab("menu")}
            data-testid="tab-menu"
            className={`px-5 py-3 label-mono inline-flex items-center gap-2 border-b-2 ${
              activeTab === "menu"
                ? "border-[#FF4500] text-white"
                : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <ImageIcon size={14} /> MENU
          </button>
          <button
            onClick={() => setActiveTab("blog")}
            data-testid="tab-blog"
            className={`px-5 py-3 label-mono inline-flex items-center gap-2 border-b-2 ${
              activeTab === "blog"
                ? "border-[#FF4500] text-white"
                : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <FileText size={14} /> BLOG
          </button>
          <RegenerateDeckPdfButton />
        </div>

        {activeTab === "blog" ? (
          <BlogTab />
        ) : activeTab === "menu" ? (
          <MenuTab />
        ) : activeTab === "newsletter" ? (
          <NewsletterTab onChange={fetchAll} />
        ) : (
        <>
        {/* Toolbar */}
        <div className="mt-6 border-y border-white/10 py-4 flex flex-wrap gap-3 items-center">
          <div className="flex flex-wrap gap-1">
            {["all", "investor", "homechef", "franchisee"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`filter-type-${k}`}
                className={`px-4 py-2 label-mono border ${
                  filter === k
                    ? "bg-[#FF4500] text-black border-[#FF4500]"
                    : "border-white/20 hover:border-white/50"
                }`}
              >
                {k.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="flex gap-1">
            {["all", "pending", "reviewed"].map((k) => (
              <button
                key={k}
                onClick={() => setReviewedFilter(k)}
                data-testid={`filter-status-${k}`}
                className={`px-4 py-2 label-mono border ${
                  reviewedFilter === k
                    ? "bg-white text-black border-white"
                    : "border-white/20 hover:border-white/50"
                }`}
              >
                {k.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="flex-1 min-w-[200px] flex items-center border border-white/20 px-3 h-10">
            <Search size={14} className="text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search"
              placeholder="Search name, email, phone..."
              className="bg-transparent outline-none text-sm flex-1 ml-2 placeholder:text-white/30"
            />
          </div>

          <button
            onClick={exportCsv}
            data-testid="admin-export-csv"
            className="px-4 h-10 bg-[#FFC107] text-black font-display inline-flex items-center gap-2 hover:bg-[#FF4500]"
          >
            <Download size={16} /> EXPORT CSV
          </button>

          <button
            onClick={pickAllVisible}
            data-testid="admin-select-all"
            className="px-3 h-10 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] label-mono"
            title="Select all visible"
          >
            SELECT ALL
          </button>
        </div>

        {/* Bulk action bar */}
        {picked.size > 0 && (
          <div className="mt-4 border border-[#FF4500] bg-[#1a0d05] px-5 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="bulk-action-bar">
            <div className="label-mono text-[#FF4500]">— {picked.size} SELECTED</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => bulkAction("review")} data-testid="bulk-review" className="border border-[#22c55e]/60 text-[#22c55e] hover:bg-[#22c55e] hover:text-black px-3 py-2 label-mono inline-flex items-center gap-2">
                <Check size={14} /> MARK REVIEWED
              </button>
              <button onClick={() => bulkAction("unreview")} data-testid="bulk-unreview" className="border border-white/30 hover:border-white px-3 py-2 label-mono">
                MARK PENDING
              </button>
              <button onClick={() => bulkAction("delete")} data-testid="bulk-delete" className="border border-[#FF4500]/60 text-[#FF4500] hover:bg-[#FF4500] hover:text-black px-3 py-2 label-mono inline-flex items-center gap-2">
                <Trash2 size={14} /> DELETE
              </button>
              <button onClick={clearPicks} data-testid="bulk-clear" className="border border-white/20 hover:border-white p-2" aria-label="Clear">
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          <div className={`${selected ? "lg:col-span-7" : "lg:col-span-12"} space-y-2`}>
            {loading ? (
              <div className="text-white/50 py-20 text-center label-mono">LOADING...</div>
            ) : filtered.length === 0 ? (
              <div className="text-white/50 py-20 text-center border border-dashed border-white/10">
                <div className="label-mono">NO SUBMISSIONS MATCH</div>
              </div>
            ) : (
              filtered.map((i) => {
                const meta = TYPE_META[i.type] || TYPE_META.investor;
                const isOpen = selected?.id === i.id;
                const isPicked = picked.has(i.id);
                const wa = buildWhatsAppLink(i);
                return (
                  <div
                    key={i.id}
                    data-testid={`submission-row-${i.id}`}
                    className={`group border ${
                      isOpen ? "border-[#FF4500] bg-[#1a0d05]" : isPicked ? "border-[#FFC107]/60 bg-[#1a1305]" : "border-white/10 bg-[#0c0c0d] hover:border-white/30"
                    } px-3 py-3 flex items-center gap-3 transition-colors`}
                  >
                    <input
                      type="checkbox"
                      checked={isPicked}
                      onChange={(e) => { e.stopPropagation(); togglePick(i.id); }}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Select"
                      data-testid={`row-pick-${i.id}`}
                      className="h-4 w-4 accent-[#FF4500] cursor-pointer"
                    />
                    <div
                      className="label-mono px-2 py-1 text-black shrink-0"
                      style={{ background: meta.color }}
                    >
                      {meta.label}
                    </div>
                    <button
                      onClick={() => setSelected(i)}
                      className="flex-1 min-w-0 text-left"
                      data-testid={`row-open-${i.id}`}
                    >
                      <div className="font-display text-lg truncate">{i.name}</div>
                      <div className="text-white/50 text-xs truncate flex gap-3">
                        <span className="inline-flex items-center gap-1"><Mail size={11} />{i.email}</span>
                        <span className="inline-flex items-center gap-1 hidden sm:inline-flex"><Phone size={11} />{i.phone}</span>
                      </div>
                    </button>
                    <div className="hidden lg:block text-right shrink-0">
                      <div className="label-mono text-[10px]">{new Date(i.submitted_at).toLocaleDateString()}</div>
                    </div>
                    {/* Quick contact icons */}
                    <div className="hidden md:flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <a href={`tel:${i.phone}`} onClick={(e) => e.stopPropagation()} aria-label="Call" data-testid={`row-call-${i.id}`} className="border border-white/15 hover:border-[#FF4500] hover:text-[#FF4500] p-2"><Phone size={12} /></a>
                      <a href={`mailto:${i.email}`} onClick={(e) => e.stopPropagation()} aria-label="Email" data-testid={`row-email-${i.id}`} className="border border-white/15 hover:border-[#FF4500] hover:text-[#FF4500] p-2"><Mail size={12} /></a>
                      {wa && (
                        <a href={wa} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} aria-label="WhatsApp" data-testid={`row-wa-${i.id}`} className="border border-white/15 hover:border-[#25D366] hover:text-[#25D366] p-2"><MessageCircle size={12} /></a>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleReview(i.id, i.reviewed); }}
                        aria-label={i.reviewed ? "Mark pending" : "Mark reviewed"}
                        data-testid={`row-review-${i.id}`}
                        className={`border p-2 ${i.reviewed ? "border-[#22c55e]/60 text-[#22c55e]" : "border-white/15 hover:border-[#22c55e] hover:text-[#22c55e]"}`}
                      >
                        <Check size={12} />
                      </button>
                    </div>
                    {i.reviewed ? (
                      <span className="label-mono text-[#22c55e] border border-[#22c55e]/40 px-2 py-1 text-[10px] shrink-0 hidden sm:inline">✓</span>
                    ) : (
                      <span className="label-mono text-[#FF4500] border border-[#FF4500]/40 px-2 py-1 text-[10px] shrink-0 hidden sm:inline">!</span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start border border-white/10 bg-[#0c0c0d] p-6 md:p-8 max-h-[80vh] overflow-y-auto" data-testid="submission-detail">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="label-mono text-[#FF4500]">/ {(TYPE_META[selected.type] || {}).label} INTEREST</div>
                  <div className="font-display text-3xl mt-2 leading-none">{selected.name}</div>
                  <div className="label-mono mt-1">{new Date(selected.submitted_at).toLocaleString()}</div>
                </div>
                <button onClick={() => setSelected(null)} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
              </div>

              <div className="mt-6 space-y-3">
                <DetailRow icon={<Mail size={14} />} label="Email" value={<a href={`mailto:${selected.email}`} className="hover:text-[#FF4500]">{selected.email}</a>} />
                <DetailRow icon={<Phone size={14} />} label="Phone" value={<a href={`tel:${selected.phone}`} className="hover:text-[#FF4500]">{selected.phone}</a>} />
                {selected.attribution && (
                  <div className="grid grid-cols-3 gap-3 items-start border-b border-white/5 pb-2">
                    <div className="label-mono text-[#FFC107]">Attribution</div>
                    <div className="col-span-2">
                      <span className="bg-[#FFC107] text-black label-mono px-2 py-1 break-all">
                        {selected.attribution}
                      </span>
                    </div>
                  </div>
                )}
                {selected.city && <DetailRow label="City" value={selected.city} />}
                {selected.organization && <DetailRow label="Organization" value={selected.organization} />}
                {selected.linkedin_url && (
                  <DetailRow
                    label="LinkedIn"
                    value={
                      <a
                        href={selected.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[#FF4500] hover:underline break-all"
                      >
                        {selected.linkedin_url}
                      </a>
                    }
                  />
                )}
                {selected.investment_range && <DetailRow label="Investment range" value={selected.investment_range} />}
                {selected.experience && <DetailRow label="Experience" value={selected.experience} />}
                {selected.specialty && <DetailRow label="Specialty" value={selected.specialty} />}
                {selected.location && <DetailRow label="Preferred location" value={selected.location} />}
                {selected.capital && <DetailRow label="Capital" value={selected.capital} />}

                {/* HomeChef extended (JotForm) fields */}
                {selected.type === "homechef" && (
                  <>
                    {(selected.first_name || selected.last_name) && <DetailRow label="Full name (first + last)" value={`${selected.first_name || ""} ${selected.last_name || ""}`.trim()} />}
                    {selected.age && <DetailRow label="Age" value={selected.age} />}
                    {selected.gender && <DetailRow label="Gender" value={selected.gender} />}
                    {(selected.street1 || selected.street2 || selected.state || selected.zip_code) && (
                      <DetailRow label="Address" value={[selected.street1, selected.street2, selected.city, selected.state, selected.zip_code].filter(Boolean).join(", ")} />
                    )}
                    {selected.residence_type && <DetailRow label="Residence" value={selected.residence_type} />}
                    {selected.marital_status && <DetailRow label="Status" value={selected.marital_status} />}
                    {selected.employment_status && <DetailRow label="Employment" value={selected.employment_status} />}
                    {Array.isArray(selected.languages_speak) && selected.languages_speak.length > 0 && <DetailRow label="Speaks" value={selected.languages_speak.join(", ")} />}
                    {Array.isArray(selected.languages_read) && selected.languages_read.length > 0 && <DetailRow label="Reads" value={selected.languages_read.join(", ")} />}
                    {Array.isArray(selected.appliances) && selected.appliances.length > 0 && <DetailRow label="Appliances at home" value={selected.appliances.join(", ")} />}
                    {selected.cloud_kitchen_ok && <DetailRow label="Owner OK with cloud kitchen?" value={selected.cloud_kitchen_ok} />}
                    {selected.delivery_pickup_ok && <DetailRow label="Owner OK with delivery pickup?" value={selected.delivery_pickup_ok} />}
                    {selected.hours_ok && <DetailRow label="Available 11am–10pm 7d?" value={selected.hours_ok} />}
                    {selected.salary_ok && <DetailRow label="Salary ₹10–12k OK?" value={selected.salary_ok} />}
                    {selected.cooking_experience && <DetailRow label="Cooking experience" value={selected.cooking_experience} />}
                    {selected.contract_ok && <DetailRow label="OK with NDA + non-compete?" value={selected.contract_ok} />}
                    {selected.health_issues && <DetailRow label="Health issues?" value={selected.health_issues} />}
                    {selected.start_date && <DetailRow label="Can start on" value={selected.start_date} />}
                    {selected.call_back_at && <DetailRow label="Best callback time" value={selected.call_back_at} />}
                    {(selected.kitchen_photo || selected.selfie_photo || selected.house_photo) && (
                      <div>
                        <div className="label-mono mb-2">Photos</div>
                        <div className="grid grid-cols-3 gap-2" data-testid="homechef-photos">
                          {[
                            { src: selected.kitchen_photo, label: "Kitchen" },
                            { src: selected.selfie_photo, label: "Selfie" },
                            { src: selected.house_photo, label: "House" },
                          ].filter((p) => p.src).map((p) => (
                            <a key={p.label} href={p.src} target="_blank" rel="noreferrer" className="block border border-white/20">
                              <img src={p.src} alt={p.label} className="w-full h-24 object-cover" />
                              <div className="text-[11px] text-white/60 font-mono px-2 py-1">{p.label}</div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {selected.message && (
                  <div>
                    <div className="label-mono mb-1">Message</div>
                    <div className="text-white/80 whitespace-pre-wrap">{selected.message}</div>
                  </div>
                )}
              </div>

              {/* Quick contact actions */}
              <div className="mt-8 grid grid-cols-3 gap-2" data-testid="detail-quick-actions">
                <a
                  href={`tel:${selected.phone}`}
                  className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] px-3 py-3 inline-flex items-center justify-center gap-2 label-mono"
                  data-testid="detail-call"
                >
                  <Phone size={14} /> CALL
                </a>
                <button
                  onClick={openEmail}
                  className="border border-white/20 hover:border-[#FF4500] hover:text-[#FF4500] px-3 py-3 inline-flex items-center justify-center gap-2 label-mono"
                  data-testid="detail-email"
                >
                  <Send size={14} /> EMAIL
                </button>
                {buildWhatsAppLink(selected) ? (
                  <a
                    href={buildWhatsAppLink(selected)}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366] hover:text-black px-3 py-3 inline-flex items-center justify-center gap-2 label-mono"
                    data-testid="detail-whatsapp"
                  >
                    <MessageCircle size={14} /> WHATSAPP
                  </a>
                ) : (
                  <span className="border border-white/10 text-white/30 px-3 py-3 inline-flex items-center justify-center gap-2 label-mono cursor-not-allowed">
                    <MessageCircle size={14} /> NO PHONE
                  </span>
                )}
              </div>

              {/* Notes */}
              <div className="mt-8" data-testid="notes-section">
                <div className="flex items-center gap-2 mb-3">
                  <StickyNote size={14} className="text-[#FFC107]" />
                  <span className="label-mono text-[#FFC107]">— NOTES & ACTIVITY</span>
                </div>
                <div className="space-y-2 mb-3 max-h-[300px] overflow-y-auto">
                  {(selected.notes || []).length === 0 ? (
                    <div className="text-white/40 text-sm border border-dashed border-white/10 p-4 text-center">
                      No notes yet. Add one below.
                    </div>
                  ) : (
                    (selected.notes || [])
                      .slice()
                      .sort((a, b) => (a.at < b.at ? 1 : -1))
                      .map((n) => (
                        <div key={n.id} className="border border-white/10 bg-black/40 p-3 group" data-testid={`note-${n.id}`}>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <span className="label-mono text-white/40">
                              {n.type === "email_sent" ? "📧 EMAIL SENT" : "✍ NOTE"}
                              {" · "}{new Date(n.at).toLocaleString()}
                            </span>
                            <button
                              onClick={() => removeNote(n.id)}
                              className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-[#FF4500] transition-opacity"
                              aria-label="Delete note"
                              data-testid={`note-delete-${n.id}`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                          <div className="text-white/85 text-sm whitespace-pre-wrap break-words">
                            {n.type === "email_sent" ? `Subject: ${n.subject}` : n.text}
                          </div>
                        </div>
                      ))
                  )}
                </div>
                <div className="flex gap-2">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add an internal note (e.g. 'Called on 28 Apr — interested, will revert next week')"
                    rows={2}
                    data-testid="note-input"
                    className="flex-1 bg-black/40 border border-white/20 text-white p-3 text-sm focus:outline-none focus:border-[#FFC107] resize-none"
                  />
                  <button
                    onClick={addNote}
                    disabled={!noteText.trim()}
                    data-testid="note-add"
                    className="bg-[#FFC107] text-black font-display px-4 inline-flex items-center gap-2 disabled:opacity-40"
                  >
                    <Plus size={14} /> ADD
                  </button>
                </div>
              </div>

              <div className="mt-8 flex gap-2">
                <button
                  onClick={() => toggleReview(selected.id, selected.reviewed)}
                  data-testid="submission-toggle-review"
                  className={`flex-1 px-4 py-3 font-display inline-flex items-center justify-center gap-2 border ${
                    selected.reviewed
                      ? "bg-transparent border-white/30 text-white"
                      : "bg-[#22c55e] text-black border-[#22c55e]"
                  }`}
                >
                  <Check size={16} /> {selected.reviewed ? "MARK PENDING" : "MARK REVIEWED"}
                </button>
                <button
                  onClick={() => remove(selected.id)}
                  data-testid="submission-delete"
                  className="px-4 py-3 border border-[#FF4500]/40 text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center gap-2"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </aside>
          )}
        </div>
        </>
        )}

        {/* Email modal */}
        {emailModal && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEmailModal(null)}>
            <div className="bg-[#0a0a0a] border border-[#FF4500] max-w-2xl w-full p-6 md:p-8" onClick={(e) => e.stopPropagation()} data-testid="email-modal">
              <div className="flex items-center justify-between mb-4">
                <div className="label-mono text-[#FF4500]">— SEND FOLLOW-UP TO {selected?.email}</div>
                <button onClick={() => setEmailModal(null)} className="text-white/50 hover:text-white" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <label className="block mb-3">
                <div className="label-mono mb-1">Subject</div>
                <input
                  value={emailModal.subject}
                  onChange={(e) => setEmailModal({ ...emailModal, subject: e.target.value })}
                  data-testid="email-subject"
                  className="w-full bg-black/40 border border-white/20 text-white h-11 px-3 focus:outline-none focus:border-[#FF4500]"
                />
              </label>
              <label className="block mb-4">
                <div className="label-mono mb-1">Message</div>
                <textarea
                  value={emailModal.body}
                  onChange={(e) => setEmailModal({ ...emailModal, body: e.target.value })}
                  rows={12}
                  data-testid="email-body"
                  className="w-full bg-black/40 border border-white/20 text-white p-3 focus:outline-none focus:border-[#FF4500] font-mono text-sm leading-relaxed resize-none"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEmailModal(null)} data-testid="email-cancel" className="px-4 py-2.5 border border-white/20 hover:border-white label-mono">CANCEL</button>
                <button onClick={sendEmail} data-testid="email-send" className="px-5 py-2.5 bg-[#FF4500] text-black font-display inline-flex items-center gap-2">
                  <Send size={14} /> SEND NOW
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const DetailRow = ({ icon, label, value }) => (
  <div className="grid grid-cols-3 gap-3 items-start border-b border-white/5 pb-2">
    <div className="label-mono inline-flex items-center gap-2">{icon}{label}</div>
    <div className="col-span-2 text-white/90 break-words">{value}</div>
  </div>
);
